import '@ionic/react/css/ionic-swiper.css';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import './MobileHomeSummaryTiles.scss';

import { IonCard, IonCardContent, IonicSlides, IonRow } from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { selectAuthUserInfo } from 'src/redux/auth/selectors';
import { useSocket } from 'src/utils/socket';
import { FreeMode, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { GenericMessages } from '../../intl/GenericMessages';
import { SocketContextPortal } from '../../utils/socket/context/portalSocketContext';
import { SocketContextSentinel } from '../../utils/socket/context/sentinelSocketContext';
import { DqText } from '../dq-text';
import { Roles } from '@dataquiver/stoneage-roles';
import { JobMessages } from 'src/intl/JobMessages';
import { HomeMessages } from 'src/intl/HomeMessages';
import classNames from 'classnames';

const MobileHomeSummaryTiles: React.FC = () => {
  const userData = useSelector(selectAuthUserInfo);
  // Loading users count
  const [userCount, setUserCount] = useState(0);
  const [userCountFetched, setUserCountFetched] = useState(false);
  const { request: portalRequest, connected: portalConnected } = useSocket(SocketContextPortal, undefined);

  // Loading jobs count
  const [jobsCount, setJobsCount] = useState(0);
  const [liveJobsCount, setLiveJobsCount] = useState(0);
  const [jobsCountFetched, setJobsCountFetched] = useState(false);
  const [liveJobsCountFetched, setLiveJobsCountFetched] = useState(false);
  const { request: sentinelRequest, connected: sentinelConnected } = useSocket(SocketContextSentinel, undefined);
  const isManager = Roles.isManager(userData?.role);

  // Request logs count from API
  useEffect(() => {
    const abort = new AbortController();

    if (userData?.org && sentinelConnected) {
      sentinelRequest('logs searchCount', {}, { signal: abort.signal })
        .then((result: any) => {
          setJobsCount(result);
        })
        .finally(() => {
          if (!abort.signal.aborted) setJobsCountFetched(true);
        });
      sentinelRequest(
        'logs searchCount',
        { statuses: ['in-progress'], orgIds: [userData.org] },
        { signal: abort.signal },
      )
        .then((result: any) => {
          setLiveJobsCount(result);
        })
        .finally(() => {
          if (!abort.signal.aborted) setLiveJobsCountFetched(true);
        });
    }

    return () => {
      abort.abort();
    };
  }, [sentinelRequest, userData?.org, sentinelConnected]);

  // Request users count from API.
  useEffect(() => {
    const abort = new AbortController();
    // User count only appears for users > Manager role
    if (isManager && userData?.org && portalConnected) {
      portalRequest('users count', { query: { org: userData.org } }, { signal: abort.signal })
        .then((result: any) => {
          setUserCount(result);
        })
        .finally(() => {
          if (!abort.signal.aborted) setUserCountFetched(true);
        });
    }

    return () => {
      abort.abort();
    };
  }, [portalRequest, userData?.org, portalConnected, userData?.role, isManager]);

  return (
    <Swiper
      slidesPerView={isManager ? 'auto' : 2}
      spaceBetween={4}
      freeMode={true}
      navigation={true}
      observer={true}
      className="status-swiper"
      modules={[Navigation, FreeMode, IonicSlides]}
    >
      {/* SWIPER IMPLEMENTATION NOTE: IonicSlides must be last entry in ^ "modules" array if other modules are used */}
      <SwiperSlide className={classNames('home-status-swiper-slide', { 'manager-role': isManager })}>
        <IonCard className="home-status-card">
          <IonCardContent class="ion-padding-vertical">
            <IonRow>
              <DqText color="dark" variant="h3">
                {jobsCountFetched ? jobsCount : <FormattedMessage {...GenericMessages.loading} />}
              </DqText>
            </IonRow>
            <IonRow>
              <DqText className="home-status-card-title" variant="body-strong">
                <FormattedMessage {...JobMessages.jobLogsTitle} />
              </DqText>
            </IonRow>
          </IonCardContent>
        </IonCard>
      </SwiperSlide>

      <SwiperSlide className={classNames('home-status-swiper-slide', { 'manager-role': isManager })}>
        <IonCard className="home-status-card">
          <IonCardContent class="ion-padding-vertical">
            <IonRow>
              <DqText color="dark" variant="h3">
                {liveJobsCountFetched ? liveJobsCount : <FormattedMessage {...GenericMessages.loading} />}
              </DqText>
            </IonRow>
            <IonRow>
              <DqText className="home-status-card-title" variant="body-strong">
                <FormattedMessage {...JobMessages.liveJobsTitle} />
              </DqText>
            </IonRow>
          </IonCardContent>
        </IonCard>
      </SwiperSlide>
      {isManager && (
        <SwiperSlide className="home-status-swiper-slide manager-role">
          <IonCard className="home-status-card">
            <IonCardContent class="ion-padding-vertical">
              <IonRow>
                <DqText color="dark" variant="h3">
                  {userCountFetched ? userCount : <FormattedMessage {...GenericMessages.loading} />}
                </DqText>
              </IonRow>
              <IonRow>
                <DqText className="home-status-card-title" variant="body-strong">
                  <FormattedMessage {...HomeMessages.homeActiveUsersCount} />
                </DqText>
              </IonRow>
            </IonCardContent>
          </IonCard>
        </SwiperSlide>
      )}
    </Swiper>
  );
};

export default MobileHomeSummaryTiles;
